import type {
  Review,
  ServiceTicket,
  ServiceTicketAssignee,
} from '@common/types';

import { DEPARTMENT_ID } from './departments.seed';
import { PROPERTIES } from './group-scope.seed';
import { ORGANIZATIONS } from './organizations.seed';
import { CURRENT_USER_ID } from './org-users.seed';
import { TICKET_POLICY_SEED } from './ticket-policy.seed';

// Keep the demo inside rolling date filters as the app ages.
const TODAY = new Date();

function daysAgo(days: number, hours = 0, minutes = 0): string {
  const d = new Date(TODAY);
  d.setUTCDate(TODAY.getUTCDate() - days);
  d.setUTCHours(hours, minutes, 0, 0);
  return new Date(days >= 0 ? Math.min(d.getTime(), TODAY.getTime()) : d.getTime()).toISOString();
}

function daysAhead(days: number, hours = 0, minutes = 0): string {
  const d = new Date(TODAY);
  d.setUTCDate(TODAY.getUTCDate() + days);
  d.setUTCHours(hours, minutes, 0, 0);
  return d.toISOString();
}

const ANDY: ServiceTicketAssignee = { id: 'usr-1', name: 'Andy Heathershaw', initials: 'AH' };
const CHRIS: ServiceTicketAssignee = { id: 'usr-2', name: 'Chris Alexandre', initials: 'CA' };
const TRISH: ServiceTicketAssignee = { id: 'usr-3', name: 'Trish Luies', initials: 'TL' };
const RYAN: ServiceTicketAssignee = { id: 'usr-4', name: 'Ryan Steyn', initials: 'RS' };
const SUZIE: ServiceTicketAssignee = { id: 'usr-5', name: 'Suzie de Vries', initials: 'SV' };
const MARSH: ServiceTicketAssignee = { id: CURRENT_USER_ID, name: 'Aiden Marsh', initials: 'AM' };

export const SERVICE_TICKET_ASSIGNEES: readonly ServiceTicketAssignee[] = [
  ANDY,
  CHRIS,
  TRISH,
  RYAN,
  SUZIE,
  MARSH,
];

export const SERVICE_TICKET_CURRENT_USER_ID = MARSH.id;

export const REVIEWS_SEED: readonly Review[] = [
  {
    id: 'rev-001',
    source: { key: 'booking', label: 'Booking.com', initial: 'B', logoUrl: '/assets/images/logos/booking.svg' },
    receivedAt: daysAgo(16, 8, 12),
    titleOriginal: 'De beste verblijfplek voor een bezoek aan Stellenbosch, Kaapstad en de wijnregio',
    titleTranslated: 'The best place to stay for a visit to Stellenbosch, Cape Town and the wine region',
    language: 'Dutch',
    rating: 100,
    guest: {
      id: 'gst-104513806',
      displayName: 'Sander',
      fullName: 'Sander de Vries',
      language: 'Dutch',
      nationality: 'Netherlands',
      country: 'Netherlands',
    },
    sections: [
      {
        key: 'title',
        label: 'Review Title',
        sentences: [
          {
            id: 's-rev001-title',
            text: 'De beste verblijfplek voor een bezoek aan Stellenbosch, Kaapstad en de wijnregio',
            translated: 'The best place to stay for a visit to Stellenbosch, Cape Town and the wine region',
            sentiment: 'positive',
            score: 90,
          },
        ],
      },
      {
        key: 'good',
        label: 'Good',
        sentences: [
          {
            id: 's-1',
            text: 'Heel aardige en attente eigenaren en medewerkers. Toplocatie.',
            translated: 'Very nice and attentive owners and staff. Top location.',
            sentiment: 'positive',
            score: 92,
          },
        ],
      },
      {
        key: 'bad',
        label: 'Bad',
        sentences: [
          {
            id: 's-2',
            text: 'Mbt het ontbijt waren er weinig keuzes na 9 uur s ochtends.',
            translated: 'Regarding breakfast there were few choices after 9am.',
            sentiment: 'neutral',
            score: 48,
          },
        ],
      },
    ],
    communications: [
      {
        id: 'cmm-1',
        kind: 'management_response',
        sender: 'The Langdon · Reservations',
        at: daysAgo(2, 14, 0),
        body: 'Thank you so much, Sander. We are delighted you enjoyed your stay and hope to welcome you back to Stellenbosch soon.',
      },
    ],
    notes: [],
    sentiment: {
      overall: 'positive',
      score: 86,
      topWords: [
        { text: 'attentive', weight: 1, sentiment: 'positive', count: 3 },
        { text: 'location', weight: 0.9, sentiment: 'positive', count: 2 },
        { text: 'nice', weight: 0.8, sentiment: 'positive', count: 2 },
        { text: 'staff', weight: 0.7, sentiment: 'positive', count: 2 },
        { text: 'owners', weight: 0.6, sentiment: 'positive', count: 1 },
        { text: 'top', weight: 0.55, sentiment: 'positive', count: 1 },
        { text: 'visit', weight: 0.5, sentiment: 'positive', count: 1 },
        { text: 'breakfast', weight: 0.45, sentiment: 'neutral', count: 1 },
      ],
    },
    travelType: 'Leisure trip',
    status: 'responded',
  },
  {
    id: 'rev-002',
    source: { key: 'tripadvisor', label: 'TripAdvisor', initial: 'T', logoUrl: '/assets/images/logos/tripadvisor.svg' },
    receivedAt: daysAgo(48, 11, 0),
    titleOriginal: 'A near-perfect weekend, marred by housekeeping',
    language: 'English',
    rating: 60,
    guest: {
      id: 'gst-2199',
      displayName: 'amy g',
      fullName: 'Amy Greenfield',
      language: 'English',
      nationality: 'United Kingdom',
      country: 'United Kingdom',
    },
    sections: [
      {
        key: 'title',
        label: 'Review Title',
        sentences: [
          {
            id: 's-rev002-title',
            text: 'A near-perfect weekend, marred by housekeeping',
            sentiment: 'neutral',
            score: 42,
          },
        ],
      },
      {
        key: 'good',
        label: 'Good',
        sentences: [
          {
            id: 's-3',
            text: 'The bar staff were lovely and the breakfast spread was very impressive.',
            sentiment: 'positive',
            score: 80,
          },
        ],
      },
      {
        key: 'bad',
        label: 'Bad',
        sentences: [
          {
            id: 's-4',
            text: 'Walked into a dirty room with a half-empty bottle on the desk.',
            sentiment: 'negative',
            score: 12,
          },
          {
            id: 's-5',
            text: 'Bathroom tiles were grimy and there was a wet patch beside the bed.',
            sentiment: 'negative',
            score: 8,
          },
        ],
      },
    ],
    communications: [
      {
        id: 'cmm-rev002-1',
        kind: 'management_response',
        sender: 'The Langdon · Front Office',
        at: daysAgo(46, 11, 0),
        body: "Amy, I'm so sorry your room wasn't ready to standard. Our housekeeping lead has re-inspected every Floor 3 suite this morning and we'd love to host you for a complimentary weekend on your next visit to make this right.",
      },
      {
        id: 'cmm-rev002-2',
        kind: 'guest_reply',
        sender: 'Amy Greenfield',
        at: daysAgo(44, 18, 15),
        body: "Thank you for the swift response — it means a lot to know it's been taken seriously. Looking forward to the next stay.",
      },
      {
        id: 'cmm-rev002-3',
        kind: 'management_response',
        sender: 'The Langdon · General Manager',
        at: daysAgo(43, 9, 45),
        body: 'Amy — a quick follow-up to confirm your complimentary weekend is on file. I have asked the team to keep an eye out when you next book so we can take care of you personally.',
      },
      {
        id: 'cmm-rev002-4',
        kind: 'system',
        sender: 'GuestRevu',
        at: daysAgo(42, 9, 5),
        body: 'Ticket TKT-104513809 marked resolved after housekeeping retraining completed.',
      },
    ],
    notes: [
      {
        id: 'note-1',
        author: 'Andy Heathershaw',
        at: daysAgo(45, 9, 30),
        body: 'Spoke to housekeeping lead. Room was flagged as cleaned but never re-inspected. Escalated to floor supervisor.',
      },
    ],
    sentiment: {
      overall: 'negative',
      score: 24,
      topWords: [
        { text: 'dirty', weight: 1, sentiment: 'negative', count: 4 },
        { text: 'grimy', weight: 0.9, sentiment: 'negative', count: 2 },
        { text: 'bottle', weight: 0.7, sentiment: 'negative', count: 1 },
        { text: 'bathroom', weight: 0.65, sentiment: 'negative', count: 2 },
        { text: 'bar', weight: 0.6, sentiment: 'positive', count: 2 },
        { text: 'breakfast', weight: 0.55, sentiment: 'positive', count: 3 },
        { text: 'lovely', weight: 0.5, sentiment: 'positive', count: 1 },
        { text: 'wet', weight: 0.45, sentiment: 'negative', count: 1 },
      ],
    },
    travelType: 'Couple',
    status: 'received',
  },
  {
    id: 'rev-003',
    source: { key: 'google', label: 'Google', initial: 'G', logoUrl: '/assets/images/logos/google.svg' },
    receivedAt: daysAgo(10, 16, 22),
    titleOriginal: 'Lovely property, broken lightbulb',
    language: 'English',
    rating: 95,
    guest: {
      id: 'gst-7710',
      displayName: 'Avila Avila',
      fullName: 'Avila Avila',
      language: 'Spanish',
      nationality: 'Argentina',
      country: 'Argentina',
    },
    sections: [
      {
        key: 'title',
        label: 'Review Title',
        sentences: [
          {
            id: 's-rev003-title',
            text: 'Lovely property, broken lightbulb',
            sentiment: 'positive',
            score: 70,
          },
        ],
      },
      {
        key: 'good',
        label: 'Good',
        sentences: [
          {
            id: 's-6',
            text: 'Beautiful gardens and the most comfortable bed I have slept in.',
            sentiment: 'positive',
            score: 95,
          },
        ],
      },
      {
        key: 'bad',
        label: 'Bad',
        sentences: [
          {
            id: 's-7',
            text: 'One small thing — the lightbulb in the bathroom mirror was out the whole stay.',
            sentiment: 'neutral',
            score: 45,
          },
        ],
      },
    ],
    communications: [
      {
        id: 'cmm-2',
        kind: 'management_response',
        sender: 'The Langdon · Front Office',
        at: daysAgo(7, 10, 0),
        body: 'Avila, thank you for letting us know. The bulb has been replaced and we are running a full mirror-light audit this week.',
      },
    ],
    notes: [],
    sentiment: {
      overall: 'positive',
      score: 78,
      topWords: [
        { text: 'beautiful', weight: 1, sentiment: 'positive' },
        { text: 'comfortable', weight: 0.95, sentiment: 'positive' },
        { text: 'bed', weight: 0.7, sentiment: 'positive' },
        { text: 'bulb', weight: 0.5, sentiment: 'neutral' },
      ],
    },
    travelType: 'Solo traveller',
    status: 'responded',
  },
  {
    id: 'rev-004',
    source: { key: 'direct', label: 'Direct Survey', initial: 'D' },
    receivedAt: daysAgo(5, 19, 50),
    titleOriginal: 'AC issue in 304',
    language: 'English',
    rating: 70,
    guest: {
      id: 'gst-8841',
      displayName: 'Kennnt',
      fullName: 'Kenneth Park',
      language: 'English',
      nationality: 'United States',
      country: 'United States',
    },
    sections: [
      {
        key: 'title',
        label: 'Review Title',
        sentences: [
          {
            id: 's-rev004-title',
            text: 'AC issue in 304',
            sentiment: 'negative',
            score: 30,
          },
        ],
      },
      {
        key: 'good',
        label: 'Good',
        sentences: [
          {
            id: 's-8',
            text: 'Staff handled the issue very professionally once flagged.',
            sentiment: 'positive',
            score: 78,
          },
        ],
      },
      {
        key: 'bad',
        label: 'Bad',
        sentences: [
          {
            id: 's-9',
            text: 'AC in room 304 was running but not cooling — slept in 27 degree heat for two nights.',
            sentiment: 'negative',
            score: 20,
          },
        ],
      },
    ],
    communications: [],
    notes: [],
    sentiment: {
      overall: 'neutral',
      score: 58,
      topWords: [
        { text: 'staff', weight: 1, sentiment: 'positive' },
        { text: 'AC', weight: 0.9, sentiment: 'negative' },
        { text: 'cooling', weight: 0.75, sentiment: 'negative' },
        { text: 'professionally', weight: 0.6, sentiment: 'positive' },
      ],
    },
    travelType: 'Business',
    status: 'received',
  },
  {
    id: 'rev-005',
    source: { key: 'expedia', label: 'Expedia', initial: 'E', logoUrl: '/assets/images/logos/expedia.svg' },
    receivedAt: daysAgo(22, 7, 30),
    titleOriginal: 'Spa booking went sideways',
    language: 'English',
    rating: 76,
    guest: {
      id: 'gst-3902',
      displayName: 'JOhn Mills',
      fullName: 'John Mills',
      language: 'English',
      nationality: 'Australia',
      country: 'Australia',
    },
    sections: [
      {
        key: 'title',
        label: 'Review Title',
        sentences: [
          {
            id: 's-rev005-title',
            text: 'Spa booking went sideways',
            sentiment: 'negative',
            score: 35,
          },
        ],
      },
      {
        key: 'good',
        label: 'Good',
        sentences: [
          {
            id: 's-10',
            text: 'The room was immaculate and the view over the gardens was stunning.',
            sentiment: 'positive',
            score: 90,
          },
        ],
      },
      {
        key: 'bad',
        label: 'Bad',
        sentences: [
          {
            id: 's-11',
            text: 'Spa appointment was double-booked and we lost the morning waiting.',
            sentiment: 'negative',
            score: 22,
          },
        ],
      },
    ],
    communications: [
      {
        id: 'cmm-3',
        kind: 'management_response',
        sender: 'The Langdon · Spa Manager',
        at: daysAgo(20, 13, 10),
        body: 'John, sincere apologies for the spa mix-up. We have audited the booking diary and comped your next visit.',
      },
    ],
    notes: [],
    sentiment: {
      overall: 'neutral',
      score: 62,
      topWords: [
        { text: 'immaculate', weight: 1, sentiment: 'positive' },
        { text: 'spa', weight: 0.85, sentiment: 'negative' },
        { text: 'view', weight: 0.7, sentiment: 'positive' },
        { text: 'double-booked', weight: 0.6, sentiment: 'negative' },
      ],
    },
    travelType: 'Couple',
    status: 'responded',
  },
];

interface TicketSeedInput extends Partial<ServiceTicket> {
  id: string;
  reviewId: string | null;
  subject: string;
  status: ServiceTicket['status'];
  urgency: ServiceTicket['urgency'];
  departmentId: string | null;
  assignee: ServiceTicketAssignee | null;
  guestName: string;
  rating: number | null;
  actionBefore: string;
  createdAt: string;
  resolvedAt?: string | null;
  watcherIds?: readonly string[];
  isMine?: boolean;
  isWatching?: boolean;
  lastActionNote?: string | null;
  actions?: ServiceTicket['actions'];
  notes?: ServiceTicket['notes'];
}

function buildTicket(input: TicketSeedInput): ServiceTicket {
  const actor = input.submittedBy ?? MARSH;
  const actions = input.actions ?? [
    { id: `${input.id}-created`, type: 'created' as const, actor, at: input.createdAt },
    ...(input.resolvedAt
      ? [
          {
            id: `${input.id}-resolved`,
            type: 'resolved' as const,
            actor: input.assignee ?? actor,
            at: input.resolvedAt,
            note: input.lastActionNote ?? undefined,
          },
        ]
      : []),
  ];
  const resolution =
    input.resolution ??
    (input.resolvedAt
      ? {
          feedbackType: input.initialFeedbackType ?? 'complaint',
          dateOfFirstReply: input.createdAt,
          dateOfResolution: input.resolvedAt,
          compensationGiven: false,
          compensationTypes: [],
          compensationValues: {},
          sentiment: 'satisfied_no_comp' as const,
          mdCeoLetter: false,
          closingNotes: input.lastActionNote ?? 'Request completed and confirmed with the guest.',
          csat: null,
          guestContacted: 'yes' as const,
          updatedAt: input.resolvedAt,
          updatedBy: input.assignee ?? actor,
        }
      : undefined);
  const organizationId = input.organizationId ?? 'sun-intl';
  const accountId = input.accountId ?? 'sun-001';
  const sla = TICKET_POLICY_SEED.slaTargets.find((target) => target.urgency === input.urgency)!;
  return {
    organizationId,
    accountId,
    propertyId: accountId,
    workflow: 'central_complaint',
    intakeChannel: 'form',
    questionnaireId: null,
    submissionId: null,
    guestId: null,
    guestEmail: '',
    guestPhone: '',
    bookingReference: '',
    initialFeedbackType: null,
    issueCategories: [],
    version: actions.length,
    updatedAt: actions.reduce(
      (latest, action) => (action.at > latest ? action.at : latest),
      input.createdAt,
    ),
    submittedBy: actor,
    queueId:
      input.workflow === 'in_the_moment'
        ? `${organizationId}-${accountId}`
        : `${organizationId}-central`,
    parentTicketId: null,
    caseId: input.id,
    conversationId: null,
    inboundMessages: [],
    readMessageIdsByUser: {},
    needsResponse: false,
    handledMessageIds: [],
    lastGuestReplyAt: null,
    firstResponseDueAt: new Date(
      Date.parse(input.createdAt) + sla.firstResponseHours * 3600000,
    ).toISOString(),
    escalationRecipientId: null,
    escalationRoleLabel: 'General Manager',
    escalationKeys: [],
    resolution,
    resolutionHistory:
      resolution && input.resolvedAt
        ? [
            {
              closedAt: input.resolvedAt,
              status: input.status,
              resolution: structuredClone(resolution),
            },
          ]
        : [],
    evidence: [],
    resolvedAt: input.resolvedAt ?? null,
    watcherIds: input.watcherIds ?? [],
    isMine: input.isMine ?? input.assignee?.id === SERVICE_TICKET_CURRENT_USER_ID,
    isWatching:
      input.isWatching ?? input.watcherIds?.includes(SERVICE_TICKET_CURRENT_USER_ID) ?? false,
    lastActionNote: input.lastActionNote ?? null,
    actions,
    notes:
      input.notes ??
      (input.lastActionNote
        ? [
            {
              id: `${input.id}-note`,
              author: input.assignee ?? actor,
              at: actions.at(-1)?.at ?? input.createdAt,
              body: input.lastActionNote,
            },
          ]
        : []),
    ...input,
  };
}

const REVIEW_TICKETS: readonly ServiceTicket[] = [
  buildTicket({
    id: 'TKT-104513806',
    reviewId: 'rev-001',
    subject: 'Translate breakfast feedback for kitchen team',
    status: 'resolved',
    urgency: 'escalated',
    departmentId: DEPARTMENT_ID.FOOD_BEVERAGE,
    assignee: CHRIS,
    guestName: 'Sander de Vries',
    rating: 100,
    createdAt: daysAgo(15, 13, 39),
    actionBefore: daysAgo(13, 17, 0),
    resolvedAt: daysAgo(12, 9, 20),
    watcherIds: [TRISH.id, MARSH.id],
    lastActionNote: 'Translation circulated to kitchen and reservations.',
    actions: [
      { id: 'a-1', type: 'created', actor: CHRIS, at: daysAgo(15, 13, 39) },
      { id: 'a-2', type: 'escalated', actor: ANDY, at: daysAgo(14, 9, 10), note: 'Surface to kitchen lead for breakfast roster.' },
      { id: 'a-3', type: 'resolved', actor: CHRIS, at: daysAgo(12, 9, 20), note: 'Done.' },
    ],
  }),
  buildTicket({
    id: 'TKT-104513807',
    reviewId: 'rev-003',
    subject: 'Replace bathroom mirror lightbulb — Suite 12',
    status: 'resolved',
    urgency: 'escalated',
    departmentId: DEPARTMENT_ID.MAINTENANCE,
    assignee: RYAN,
    guestName: 'Avila Avila',
    rating: 95,
    createdAt: daysAgo(9, 11, 24),
    actionBefore: daysAgo(7, 13, 0),
    resolvedAt: daysAgo(7, 16, 5),
    lastActionNote: 'Bulb replaced. Audit of all suite mirrors scheduled for Friday.',
    actions: [
      { id: 'a-4', type: 'created', actor: RYAN, at: daysAgo(9, 11, 24) },
      { id: 'a-5', type: 'resolved', actor: RYAN, at: daysAgo(7, 16, 5), note: 'Bulb replaced.' },
    ],
  }),
  buildTicket({
    id: 'TKT-104513808',
    reviewId: null,
    subject: 'Late checkout request — room 218 anniversary',
    status: 'assigned',
    urgency: 'escalated',
    departmentId: DEPARTMENT_ID.FRONT_DESK,
    assignee: TRISH,
    guestName: 'Anonymous',
    rating: null,
    createdAt: daysAgo(3, 15, 6),
    actionBefore: daysAhead(1, 23, 0),
    watcherIds: [MARSH.id],
    lastActionNote: 'Awaiting confirmation from front office.',
    actions: [
      { id: 'a-6', type: 'created', actor: TRISH, at: daysAgo(3, 15, 6) },
      { id: 'a-7', type: 'assigned', actor: TRISH, at: daysAgo(3, 15, 7), note: 'Holding for front office sign-off.' },
    ],
  }),
  buildTicket({
    id: 'TKT-104513809',
    reviewId: 'rev-002',
    subject: 'Address cleanliness issues — dirty room, grimy bathroom, wet spot',
    status: 'resolved',
    urgency: 'escalated',
    departmentId: DEPARTMENT_ID.HOUSEKEEPING,
    assignee: ANDY,
    guestName: 'Amy Greenfield',
    rating: 24,
    createdAt: daysAgo(47, 12, 34),
    actionBefore: daysAgo(45, 16, 0),
    resolvedAt: daysAgo(42, 9, 0),
    watcherIds: [MARSH.id, RYAN.id],
    lastActionNote: 'Housekeeping retrained. Floor 3 re-inspection log live.',
    actions: [
      { id: 'a-8', type: 'created', actor: ANDY, at: daysAgo(47, 12, 34) },
      { id: 'a-9', type: 'escalated', actor: ANDY, at: daysAgo(46, 9, 0), note: 'Escalated to housekeeping lead.' },
      { id: 'a-10', type: 'note_added', actor: ANDY, at: daysAgo(45, 9, 30), note: 'Spoke to lead. Inspection process gap identified.' },
      { id: 'a-11', type: 'resolved', actor: ANDY, at: daysAgo(42, 9, 0), note: 'Retraining done.' },
    ],
  }),
  buildTicket({
    id: 'TKT-104513810',
    reviewId: 'rev-004',
    subject: 'AC unit room 304 — replace fan motor',
    status: 'in_progress',
    urgency: 'critical',
    departmentId: DEPARTMENT_ID.MAINTENANCE,
    assignee: MARSH,
    guestName: 'Kenneth Park',
    rating: 58,
    createdAt: daysAgo(4, 8, 2),
    actionBefore: daysAhead(0, 17, 0),
    watcherIds: [ANDY.id, RYAN.id],
    lastActionNote: 'Parts ordered. Engineer on site Thursday.',
    actions: [
      { id: 'a-12', type: 'created', actor: SUZIE, at: daysAgo(4, 8, 2) },
      { id: 'a-13', type: 'assigned', actor: ANDY, at: daysAgo(4, 8, 30), note: 'Assigned to maintenance.' },
      { id: 'a-14', type: 'reassigned', actor: ANDY, at: daysAgo(3, 14, 0), note: 'Reassigned to Aiden — owns vendor relationship.' },
      { id: 'a-15', type: 'note_added', actor: MARSH, at: daysAgo(2, 11, 0), note: 'Parts ordered. Engineer Thursday.' },
    ],
  }),
  buildTicket({
    id: 'TKT-104513811',
    reviewId: null,
    subject: 'Wi-Fi signal weak across north wing',
    status: 'assigned',
    urgency: 'normal',
    departmentId: DEPARTMENT_ID.MAINTENANCE,
    assignee: RYAN,
    guestName: 'Kenneth Park',
    rating: null,
    createdAt: daysAgo(2, 7, 14),
    actionBefore: daysAhead(3, 17, 0),
    lastActionNote: 'Survey scheduled.',
    actions: [
      { id: 'a-16', type: 'created', actor: RYAN, at: daysAgo(2, 7, 14) },
    ],
  }),
  buildTicket({
    id: 'TKT-104513812',
    reviewId: 'rev-005',
    subject: 'Spa double-booking — restitution for Mills',
    status: 'resolved',
    urgency: 'escalated',
    departmentId: DEPARTMENT_ID.SPA_WELLNESS,
    assignee: SUZIE,
    guestName: 'John Mills',
    rating: 76,
    createdAt: daysAgo(21, 10, 4),
    actionBefore: daysAgo(20, 17, 0),
    resolvedAt: daysAgo(19, 12, 0),
    lastActionNote: 'Comped next-visit spa booking confirmed.',
    actions: [
      { id: 'a-17', type: 'created', actor: SUZIE, at: daysAgo(21, 10, 4) },
      { id: 'a-18', type: 'resolved', actor: SUZIE, at: daysAgo(19, 12, 0), note: 'Comp booked.' },
    ],
  }),
  buildTicket({
    id: 'TKT-104513813',
    reviewId: null,
    subject: 'Mini-bar charges disputed — invoice 88312',
    status: 'in_progress',
    urgency: 'normal',
    departmentId: DEPARTMENT_ID.FRONT_DESK,
    assignee: TRISH,
    guestName: 'Kenneth Park',
    rating: null,
    createdAt: daysAgo(1, 18, 22),
    actionBefore: daysAhead(2, 17, 0),
    actions: [
      { id: 'a-19', type: 'created', actor: TRISH, at: daysAgo(1, 18, 22) },
    ],
  }),
  buildTicket({
    id: 'TKT-104513814',
    reviewId: null,
    subject: 'Pool towels not restocked by 10am',
    status: 'assigned',
    urgency: 'normal',
    departmentId: DEPARTMENT_ID.HOUSEKEEPING,
    assignee: TRISH,
    guestName: 'Kenneth Park',
    rating: null,
    createdAt: daysAgo(1, 11, 0),
    actionBefore: daysAhead(0, 17, 0),
  }),
  buildTicket({
    id: 'TKT-104513815',
    reviewId: null,
    subject: 'Cigarette smell in non-smoking room 218',
    status: 'assigned',
    urgency: 'escalated',
    departmentId: DEPARTMENT_ID.HOUSEKEEPING,
    assignee: ANDY,
    guestName: 'Lena Schneider',
    rating: null,
    createdAt: daysAgo(1, 9, 30),
    actionBefore: daysAhead(0, 12, 0),
    watcherIds: [MARSH.id],
  }),
  buildTicket({
    id: 'TKT-104513816',
    reviewId: null,
    subject: 'Hot water intermittent — east wing showers',
    status: 'in_progress',
    urgency: 'normal',
    departmentId: DEPARTMENT_ID.MAINTENANCE,
    assignee: MARSH,
    guestName: 'Hugo Beauchamp',
    rating: null,
    createdAt: daysAgo(6, 7, 45),
    actionBefore: daysAhead(1, 17, 0),
    lastActionNote: 'Boiler tech on site tomorrow morning.',
  }),
  buildTicket({
    id: 'TKT-104513817',
    reviewId: null,
    subject: 'Restaurant booking conflict — anniversary dinner',
    status: 'resolved',
    urgency: 'normal',
    departmentId: DEPARTMENT_ID.FOOD_BEVERAGE,
    assignee: CHRIS,
    guestName: 'Mira Andersson',
    rating: 88,
    createdAt: daysAgo(8, 14, 12),
    actionBefore: daysAgo(7, 17, 0),
    resolvedAt: daysAgo(7, 11, 0),
    lastActionNote: 'Moved to private corner table, comped sparkling.',
  }),
  buildTicket({
    id: 'TKT-104513818',
    reviewId: null,
    subject: 'Stained sheets discovered on arrival — room 412',
    status: 'resolved',
    urgency: 'critical',
    departmentId: DEPARTMENT_ID.HOUSEKEEPING,
    assignee: ANDY,
    guestName: 'Diego Romano',
    rating: 35,
    createdAt: daysAgo(11, 16, 0),
    actionBefore: daysAgo(11, 18, 30),
    resolvedAt: daysAgo(11, 17, 45),
    lastActionNote: 'Linen changed, manager apology delivered in person.',
  }),
  buildTicket({
    id: 'TKT-104513819',
    reviewId: null,
    subject: 'Concierge unresponsive to spa booking',
    status: 'closed',
    urgency: 'normal',
    departmentId: DEPARTMENT_ID.GUEST_RELATIONS,
    assignee: SUZIE,
    guestName: 'Yara Khoury',
    rating: 68,
    createdAt: daysAgo(28, 9, 15),
    actionBefore: daysAgo(26, 17, 0),
    resolvedAt: daysAgo(25, 12, 30),
  }),
  buildTicket({
    id: 'TKT-104513820',
    reviewId: null,
    subject: 'TV remote batteries dead in suite 8',
    status: 'closed',
    urgency: 'normal',
    departmentId: DEPARTMENT_ID.MAINTENANCE,
    assignee: RYAN,
    guestName: 'Otis Lindberg',
    rating: null,
    createdAt: daysAgo(13, 20, 0),
    actionBefore: daysAgo(12, 12, 0),
    resolvedAt: daysAgo(12, 8, 30),
  }),
  buildTicket({
    id: 'TKT-104513821',
    reviewId: null,
    subject: 'Loud party in adjacent room past midnight',
    status: 'resolved',
    urgency: 'critical',
    departmentId: DEPARTMENT_ID.GUEST_RELATIONS,
    assignee: MARSH,
    guestName: 'Sasha Petrov',
    rating: 42,
    createdAt: daysAgo(5, 1, 12),
    actionBefore: daysAgo(5, 2, 30),
    resolvedAt: daysAgo(5, 1, 55),
    lastActionNote: 'Night manager intervened, guest moved to quieter suite.',
  }),
  buildTicket({
    id: 'TKT-104513822',
    reviewId: null,
    subject: 'Missing towels and toiletries in suite 14',
    status: 'assigned',
    urgency: 'normal',
    departmentId: DEPARTMENT_ID.HOUSEKEEPING,
    assignee: TRISH,
    guestName: 'Camille Lefevre',
    rating: null,
    createdAt: daysAgo(0, 14, 18),
    actionBefore: daysAhead(0, 18, 0),
  }),
  buildTicket({
    id: 'TKT-104513823',
    reviewId: null,
    subject: 'Wedding party noise complaint — terrace event',
    status: 'in_progress',
    urgency: 'escalated',
    departmentId: DEPARTMENT_ID.GUEST_RELATIONS,
    assignee: MARSH,
    guestName: 'Bruno Ricci',
    rating: null,
    createdAt: daysAgo(2, 22, 30),
    actionBefore: daysAhead(0, 22, 0),
    watcherIds: [ANDY.id],
    lastActionNote: 'Events team aware. Curfew shifted to 22:30 for terrace events.',
  }),
];

function hoursFromNow(hours: number): string {
  return new Date(TODAY.getTime() + hours * 3600000).toISOString();
}

function buildPropertyTickets(
  organizationId: string,
  accountId: string,
  index: number,
): ServiceTicket[] {
  const prefix = `TKT-${organizationId}-${accountId}`;
  const guestName = [
    'Naledi Mokoena',
    'Oliver Bennett',
    'Priya Naidoo',
    'Lucas Meyer',
    'Fatima Khan',
  ][index % 5];
  const room = 200 + index;
  const common = {
    organizationId,
    accountId,
    reviewId: null,
    guestName,
    guestEmail: `demo.guest.${index + 1}@example.com`,
    bookingReference: `DEMO-${organizationId}-${accountId}`,
    rating: null,
    initialFeedbackType: 'complaint' as const,
    watcherIds: [MARSH.id],
  };
  const complaintId = `${prefix}-03`;
  const conversationId = `${complaintId}-conversation`;
  const messageId = `${complaintId}-reply-1`;
  const replyAt = hoursFromNow(-1);
  return [
    buildTicket({
      ...common,
      id: `${prefix}-01`,
      subject: `Extra towels requested for room ${room}`,
      workflow: 'in_the_moment',
      intakeChannel: 'phone',
      initialFeedbackType: 'general',
      status: 'new',
      urgency: 'normal',
      departmentId: DEPARTMENT_ID.HOUSEKEEPING,
      assignee: null,
      createdAt: hoursFromNow(-0.5),
      actionBefore: hoursFromNow(3),
      lastActionNote:
        'Guest called reception for two bath towels before using the pool. Awaiting a team member to claim the request.',
    }),
    buildTicket({
      ...common,
      id: `${prefix}-02`,
      subject: `Late checkout confirmation for room ${room + 1}`,
      workflow: 'in_the_moment',
      intakeChannel: 'in_person',
      initialFeedbackType: 'general',
      status: 'assigned',
      urgency: 'normal',
      departmentId: DEPARTMENT_ID.FRONT_DESK,
      assignee: TRISH,
      createdAt: hoursFromNow(-4),
      actionBefore: hoursFromNow(6),
      lastActionNote:
        'Guest requested a 14:00 checkout. Front desk is checking availability with housekeeping.',
    }),
    buildTicket({
      ...common,
      id: complaintId,
      subject: `Air conditioning still noisy in room ${room + 2}`,
      intakeChannel: 'email',
      status: 'in_progress',
      urgency: 'critical',
      departmentId: DEPARTMENT_ID.MAINTENANCE,
      assignee: MARSH,
      issueCategories: ['Air conditioning', 'Noise'],
      createdAt: hoursFromNow(-48),
      actionBefore: hoursFromNow(-2),
      conversationId,
      inboundMessages: [
        {
          ticketId: complaintId,
          conversationId,
          messageId,
          receivedAt: replyAt,
          preview:
            'The air conditioning is still rattling after the repair. Could we move to a quieter room tonight?',
        },
      ],
      needsResponse: true,
      lastGuestReplyAt: replyAt,
      lastActionNote:
        'Engineering inspected the unit. A separate investigation is tracking the fan motor; guest relations is arranging a room move.',
      actions: [
        { id: `${complaintId}-created`, type: 'created', actor: SUZIE, at: hoursFromNow(-48) },
        {
          id: `${complaintId}-assigned`,
          type: 'assigned',
          actor: ANDY,
          at: hoursFromNow(-47),
          note: 'Assigned to Aiden to coordinate engineering and the room move.',
        },
        {
          id: `${complaintId}-reply`,
          type: 'guest_reply',
          actor: {
            id: `${complaintId}-guest`,
            name: guestName,
            initials: guestName
              .split(' ')
              .map((n) => n[0])
              .join(''),
          },
          at: replyAt,
          note: 'Guest requested a quieter room.',
        },
      ],
      submittedBy: SUZIE,
    }),
    buildTicket({
      ...common,
      id: `${prefix}-04`,
      subject: `Investigate fan motor in room ${room + 2}`,
      intakeChannel: 'email',
      parentTicketId: complaintId,
      caseId: complaintId,
      status: 'assigned',
      urgency: 'escalated',
      departmentId: DEPARTMENT_ID.MAINTENANCE,
      assignee: RYAN,
      issueCategories: ['Air conditioning', 'Noise'],
      createdAt: hoursFromNow(-24),
      actionBefore: hoursFromNow(12),
      lastActionNote:
        'Check the fan bearing and confirm a replacement part. Keep guest communication on the main case.',
    }),
    buildTicket({
      ...common,
      id: `${prefix}-05`,
      guestName: 'Emma Wilson',
      guestEmail: 'emma.wilson@example.com',
      bookingReference: `DEMO-${organizationId}-${accountId}-BREAKFAST`,
      subject: 'Breakfast dietary request missed',
      intakeChannel: 'form',
      questionnaireId: 'q-001',
      submissionId: `${prefix}-breakfast-submission`,
      status: 'resolved',
      urgency: 'escalated',
      departmentId: DEPARTMENT_ID.FOOD_BEVERAGE,
      assignee: CHRIS,
      createdAt: hoursFromNow(-72),
      actionBefore: hoursFromNow(-48),
      resolvedAt: hoursFromNow(-50),
      lastActionNote:
        'Chef confirmed the allergy requirements, prepared a separate breakfast and updated the guest preference notes.',
    }),
    buildTicket({
      ...common,
      id: `${prefix}-06`,
      guestName: 'Daniel Jacobs',
      guestEmail: 'daniel.jacobs@example.com',
      bookingReference: `DEMO-${organizationId}-${accountId}-BILLING`,
      subject: 'Duplicate parking charge refunded',
      intakeChannel: 'letter',
      status: 'closed',
      urgency: 'normal',
      departmentId: DEPARTMENT_ID.FRONT_DESK,
      assignee: ANDY,
      createdAt: hoursFromNow(-120),
      actionBefore: hoursFromNow(-48),
      resolvedAt: hoursFromNow(-60),
      lastActionNote:
        'Duplicate parking charge reversed. Corrected invoice sent and receipt confirmed by the guest.',
      resolution: {
        feedbackType: 'complaint',
        dateOfFirstReply: hoursFromNow(-116),
        dateOfResolution: hoursFromNow(-60),
        compensationGiven: true,
        compensationTypes: ['refund'],
        compensationValues: { refund: 150 },
        currency: 'ZAR',
        sentiment: 'satisfied_with_comp',
        mdCeoLetter: false,
        closingNotes:
          'Refunded the duplicate parking charge of ZAR 150 and sent the corrected invoice.',
        guestContacted: 'yes',
        csat: null,
        updatedAt: hoursFromNow(-60),
        updatedBy: ANDY,
      },
    }),
  ];
}

// Mirror OrganizationService.accountsFor: demo organizations share slices of the property catalog.
// IDs include both scopes so changing organizations never reuses another organization's case.
export const SERVICE_TICKETS_SEED: readonly ServiceTicket[] = [
  ...REVIEW_TICKETS,
  ...ORGANIZATIONS.flatMap((organization) =>
    PROPERTIES.slice(0, organization.accountCount).flatMap((property, index) =>
      buildPropertyTickets(organization.id, property.id, index),
    ),
  ),
];
